
/**
 * Peer comparison and market analysis utilities
 */

import { PeerAnalysisConfig, PeerAnalysisResult } from './types';

export function peerComparisonAnalysis(
  projectData: any,
  config: PeerAnalysisConfig
): PeerAnalysisResult {
  // Get peer data based on configuration
  const directComparators = getDirectComparators(projectData, config);
  const industryBenchmarks = getIndustryBenchmarks(config);
  const valuationMultiples = getValuationMultiples(config);
  
  // Analyze market positioning
  const marketPositioning = analyzeMarketPositioning(projectData, directComparators);
  
  // Identify competitive advantages
  const competitiveAdvantages = identifyCompetitiveAdvantages(projectData, directComparators);
  
  // Assess risks relative to peers
  const risks = assessRelativeRisks(projectData, directComparators, config);
  
  return {
    directComparators,
    industryBenchmarks,
    valuationMultiples,
    marketPositioning,
    competitiveAdvantages,
    risks
  };
}

function getDirectComparators(projectData: any, config: PeerAnalysisConfig): any[] {
  return generateSamplePeerComparisonData(config.mineralType, config.projectStage);
}

function getIndustryBenchmarks(config: PeerAnalysisConfig): any[] {
  return [
    { metric: 'EV/Resource', industry: config.mineralType, value: 150, percentile: 50 },
    { metric: 'P/NAV', industry: config.mineralType, value: 0.8, percentile: 50 },
    { metric: 'Discovery Cost', industry: config.mineralType, value: 25, percentile: 50 }
  ];
}

function getValuationMultiples(config: PeerAnalysisConfig): any[] {
  return [
    { multiple: 'EV/Resource Oz', median: 120, range: [80, 200] },
    { multiple: 'EV/NAV', median: 0.9, range: [0.6, 1.4] },
    { multiple: 'P/Book', median: 1.2, range: [0.8, 2.0] }
  ];
}

function analyzeMarketPositioning(projectData: any, comparators: any[]): any {
  return {
    quartile: 2,
    percentile: 65,
    strengths: ['High grade', 'Good location'],
    weaknesses: ['Early stage', 'Limited infrastructure']
  };
}

function identifyCompetitiveAdvantages(projectData: any, comparators: any[]): string[] {
  return [
    'Superior grade potential',
    'Favorable jurisdiction',
    'Experienced management team',
    'Strategic location near infrastructure'
  ];
}

function assessRelativeRisks(projectData: any, comparators: any[], config: PeerAnalysisConfig): string[] {
  return [
    'Higher geological risk due to early stage',
    'Lower political risk in stable jurisdiction',
    'Moderate technical risk for mineral type'
  ];
}

function generateSamplePeerComparisonData(mineralType: string, projectStage: string): any[] {
  return [
    {
      name: `${mineralType} Corp A`,
      stage: projectStage,
      marketCap: 50000000,
      ev: 45000000,
      resourceOz: 500000,
      grade: 2.5,
      jurisdiction: 'Australia',
      evPerOz: 90
    },
    {
      name: `${mineralType} Corp B`,
      stage: projectStage,
      marketCap: 75000000,
      ev: 70000000,
      resourceOz: 800000,
      grade: 1.8,
      jurisdiction: 'Canada',
      evPerOz: 87.5
    }
  ];
}
